import { useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Plus, X } from 'lucide-react'
import { toast } from 'sonner'
import { LoadError } from '@/components/campaigns/LoadError'
import { formatDay, toTimeInput } from '@/components/campaigns/campaignFormat'
import { validateSettings, type SettingsErrors } from '@/components/campaigns/campaignValidation'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { useCampaignSettingsQuery, useUpdateCampaignSettings } from '@/queries'
import type { CampaignSettings } from '@/services/crmCampaignsService'
import { useParams } from 'react-router-dom'

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6]

/**
 * Horario, días no laborables, límites e interruptor de emergencia.
 *
 * Un diálogo y no una pantalla: son ocho campos que se tocan pocas veces y
 * siempre desde la lista. Solo se monta el formulario con los datos ya cargados,
 * para que el borrador no empiece vacío y pise lo guardado.
 */
export function CampaignSettingsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation()
  const settings = useCampaignSettingsQuery()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('campaigns.settings.title')}</DialogTitle>
          <DialogDescription>{t('campaigns.settings.description')}</DialogDescription>
        </DialogHeader>
        {settings.isPending ? (
          <div className="space-y-2" data-testid="settings-loading">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : settings.isError ? (
          <LoadError error={settings.error} onRetry={() => void settings.refetch()} />
        ) : (
          <SettingsForm initial={settings.data} onDone={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  )
}

function SettingsForm({ initial, onDone }: { initial: CampaignSettings; onDone: () => void }) {
  const { t } = useTranslation()
  const { lang = 'es' } = useParams()
  const update = useUpdateCampaignSettings()
  const [draft, setDraft] = useState<CampaignSettings>(initial)
  const [errors, setErrors] = useState<SettingsErrors>({})
  const [holiday, setHoliday] = useState('')

  useEffect(() => setDraft(initial), [initial])

  const set = <K extends keyof CampaignSettings>(key: K, value: CampaignSettings[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }))

  const toggleWeekday = (day: number, checked: boolean) =>
    set(
      'dias_laborables',
      checked
        ? [...draft.dias_laborables, day].sort((a, b) => a - b)
        : draft.dias_laborables.filter((item) => item !== day),
    )

  const addHoliday = () => {
    if (!holiday || draft.dias_no_laborables.includes(holiday)) return
    set('dias_no_laborables', [...draft.dias_no_laborables, holiday].sort())
    setHoliday('')
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const found = validateSettings(draft, t)
    setErrors(found)
    if (Object.keys(found).length > 0) return
    update.mutate(
      { ...draft, hora_inicio: toTimeInput(draft.hora_inicio), hora_fin: toTimeInput(draft.hora_fin) },
      {
        onSuccess: () => {
          toast.success(t('campaigns.settings.saved'))
          onDone()
        },
      },
    )
  }

  return (
    <form className="space-y-8" onSubmit={submit} noValidate>
      {update.isError ? (
        <Alert variant="destructive">
          <AlertDescription>{update.error.message}</AlertDescription>
        </Alert>
      ) : null}

      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <Label htmlFor="settings-dispatch">{t('campaigns.settings.dispatch_label')}</Label>
          <p className="text-xs text-muted-foreground">{t('campaigns.settings.dispatch_help')}</p>
        </div>
        <Switch
          id="settings-dispatch"
          aria-label={t('campaigns.settings.dispatch_label')}
          checked={draft.despacho_activo}
          onCheckedChange={(checked) => set('despacho_activo', checked)}
        />
      </div>

      <section className="space-y-4">
        <h3 className="text-lg font-semibold">{t('campaigns.settings.schedule_title')}</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="settings-start">{t('campaigns.settings.start_label')}</Label>
            <Input
              id="settings-start"
              aria-label={t('campaigns.settings.start_label')}
              type="time"
              value={toTimeInput(draft.hora_inicio)}
              onChange={(event) => set('hora_inicio', event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="settings-end">{t('campaigns.settings.end_label')}</Label>
            <Input
              id="settings-end"
              aria-label={t('campaigns.settings.end_label')}
              type="time"
              value={toTimeInput(draft.hora_fin)}
              aria-invalid={Boolean(errors.hora_fin)}
              aria-describedby={errors.hora_fin ? 'settings-end-error' : undefined}
              onChange={(event) => set('hora_fin', event.target.value)}
            />
            <FieldError id="settings-end-error">{errors.hora_fin}</FieldError>
          </div>
        </div>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">{t('campaigns.settings.workdays_label')}</legend>
          <div className="flex flex-wrap gap-4">
            {WEEKDAYS.map((day) => (
              <Label key={day} className="flex items-center gap-2 font-normal">
                <Checkbox
                  aria-label={t(`campaigns.settings.weekday.${day}`)}
                  checked={draft.dias_laborables.includes(day)}
                  onCheckedChange={(checked) => toggleWeekday(day, checked === true)}
                />
                {t(`campaigns.settings.weekday.${day}`)}
              </Label>
            ))}
          </div>
          <FieldError>{errors.dias_laborables}</FieldError>
        </fieldset>

        <div className="space-y-2">
          <Label htmlFor="settings-holiday">{t('campaigns.settings.holidays_label')}</Label>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              id="settings-holiday"
              aria-label={t('campaigns.settings.holidays_label')}
              type="date"
              className="w-44"
              value={holiday}
              onChange={(event) => setHoliday(event.target.value)}
            />
            <Button type="button" variant="outline" size="sm" disabled={!holiday} onClick={addHoliday}>
              <Plus className="mr-2 h-4 w-4" />
              {t('campaigns.settings.add_holiday')}
            </Button>
          </div>
          {draft.dias_no_laborables.length > 0 ? (
            <ul className="flex flex-wrap gap-2">
              {draft.dias_no_laborables.map((day) => (
                <li key={day}>
                  <Badge variant="secondary" className="gap-1 font-normal">
                    {formatDay(`${day}T12:00:00Z`, lang)}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      aria-label={t('campaigns.settings.remove_holiday', { day })}
                      title={t('campaigns.settings.remove_holiday', { day })}
                      onClick={() => set('dias_no_laborables', draft.dias_no_laborables.filter((item) => item !== day))}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </Badge>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-lg font-semibold">{t('campaigns.settings.limits_title')}</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="settings-daily">{t('campaigns.settings.daily_limit')}</Label>
            <Input
              id="settings-daily"
              aria-label={t('campaigns.settings.daily_limit')}
              type="number"
              min={1}
              value={draft.limite_diario}
              aria-invalid={Boolean(errors.limite_diario)}
              onChange={(event) => set('limite_diario', Number(event.target.value))}
            />
            <FieldError>{errors.limite_diario}</FieldError>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="settings-minute">{t('campaigns.settings.minute_limit')}</Label>
            <Input
              id="settings-minute"
              aria-label={t('campaigns.settings.minute_limit')}
              type="number"
              min={1}
              value={draft.limite_minuto}
              aria-invalid={Boolean(errors.limite_minuto)}
              onChange={(event) => set('limite_minuto', Number(event.target.value))}
            />
            <FieldError>{errors.limite_minuto}</FieldError>
          </div>
        </div>
      </section>

      <DialogFooter>
        <Button type="submit" disabled={update.isPending}>
          {update.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {t('campaigns.settings.save')}
        </Button>
      </DialogFooter>
    </form>
  )
}
